/**
 * ATL-1445 — the pure half of applying an Atlas API v1 draft in the app: can this person apply it
 * here and now, and what exactly is handed to the existing screen.
 *
 * Nothing here saves anything. A sale draft becomes the SEED of the ordinary New Sale cart (the
 * same once-only seeding a customer order uses, ATL-1179); an acceptance draft becomes the
 * `preselectedItems` of the ordinary acceptance form (the same shape stock movements and supply
 * orders already pass). The merchant then saves through the existing checkout / Save button.
 */
import type { ApiDraft } from './apiDraftsApi'
import type { DraftReport } from './apiDraftReports'

type ProductsDict = Record<string, Record<string, unknown> | undefined>

export type DraftApplicability =
  | { ok: true }
  | { ok: false; reason: 'not_open' | 'store_missing' | 'other_store' | 'supplier_mismatch' }
  | { ok: false; reason: 'products_missing'; missing: string[] }
  /** This device already saved it: the report is on its way, or was refused (`refusedCode`). */
  | { ok: false; reason: 'saved_here'; firestoreId: string; refusedCode?: string }

/**
 * Whether the draft can be opened now, and if not, why.
 *
 * - A draft this device already saved is never offered again, whatever the backend still says
 *   about it (see apiDraftReports.ts): a second tap would be a second sale.
 * - A sale draft opens only in the person's current store. New Sale sells there (switching is a
 *   per-store setting), and the backend refuses to link a sale saved in another store.
 * - An acceptance draft for another store opens only for someone who may work in other stores —
 *   the same `viewOtherStores` rule the rest of the app uses; the form is preset to the draft's store.
 * - A product no longer in the catalogue would make the existing cart / form code dereference
 *   `undefined` (ATL-1178), so such a draft is not opened at all; the merchant can dismiss it.
 * - An acceptance is recorded against ONE supplier, which the form takes from the first line. Lines
 *   of different suppliers (or a line without one) would book everything to that supplier — or
 *   leave the form with none — so such a draft is not opened either. The backend refuses to create
 *   one; this catches a catalogue that changed after the draft was made.
 */
export function draftApplicability(
  draft: ApiDraft,
  context: {
    productsDict: ProductsDict
    storesDict: Record<string, unknown>
    /** Required to open an acceptance draft: the form resolves its supplier here. */
    suppliersDict?: Record<string, unknown>
    currentStoreId: string | null | undefined
    canUseOtherStores: boolean
    savedHere?: DraftReport
  }
): DraftApplicability {
  if (draft.status !== 'proposed') return { ok: false, reason: 'not_open' }
  const { savedHere } = context
  if (savedHere) {
    return {
      ok: false,
      reason: 'saved_here',
      firestoreId: savedHere.firestoreId,
      ...(savedHere.refusedCode !== undefined ? { refusedCode: savedHere.refusedCode } : {}),
    }
  }
  if (!context.storesDict[draft.store.id]) return { ok: false, reason: 'store_missing' }
  const inCurrentStore = draft.store.id === context.currentStoreId
  if (!inCurrentStore && (draft.kind === 'sale' || !context.canUseOtherStores)) {
    return { ok: false, reason: 'other_store' }
  }
  const missing = draft.items
    .filter((line) => !context.productsDict[line.productId])
    .map((line) => line.name ?? line.productId)
  if (missing.length > 0) return { ok: false, reason: 'products_missing', missing }
  if (draft.kind === 'acceptance') {
    const suppliers = new Set(draft.items.map((line) => context.productsDict[line.productId]?.supplierID ?? null))
    const [only] = [...suppliers]
    if (suppliers.size !== 1 || typeof only !== 'string' || !context.suppliersDict?.[only]) {
      return { ok: false, reason: 'supplier_mismatch' }
    }
  }
  return { ok: true }
}

/**
 * The New Sale seed: the shape `newSaleFromClientOrder` consumes (`{ id, cart: [{ id, quantity }] }`),
 * keyed distinctly from any customer order so the once-only guard can never confuse the two.
 */
export function saleDraftCartSeed(draft: ApiDraft): { id: string; cart: Array<{ id: string; quantity: number }> } {
  return {
    id: `api-draft:${draft.id}`,
    cart: draft.items.map((line) => ({ id: line.productId, quantity: line.quantity })),
  }
}

/**
 * The acceptance form's `preselectedItems`: the catalogue product with the drafted quantity and,
 * when the agent proposed one, the purchase price. SupplyDetail derives prices, units and the
 * supplier from the catalogue exactly as it does for a stock-movement or supply-order hand-off.
 */
export function acceptanceDraftPreselectedItems(
  draft: ApiDraft,
  productsDict: ProductsDict
): Array<Record<string, unknown>> {
  return draft.items
    .filter((line) => productsDict[line.productId])
    .map((line) => ({
      ...(productsDict[line.productId] as Record<string, unknown>),
      id: line.productId,
      quantity: line.quantity,
      ...(line.purchasePrice !== undefined ? { purchasePrice: line.purchasePrice } : {}),
    }))
}

const KNOWN_REFUSALS = new Set([
  'predates_proposal',
  'in_other_organization',
  'not_found',
  'store_mismatch',
  'no_draft_products',
  'record_already_claimed',
  'permission_denied',
  'invalid_firestore_id',
])

/**
 * The translation key explaining why the backend would not link a saved record to its draft. The
 * record-verification codes arrive per kind (`sale_predates_proposal`, `acceptance_…`); one text
 * covers both. An unknown code gets the generic text, never a raw code on screen.
 */
export function apiDraftRefusalKey(code: string): string {
  const reason = code.replace(/^(sale|acceptance)_/, '')
  return KNOWN_REFUSALS.has(reason) ? `apiDrafts.refusal_${reason}` : 'apiDrafts.refusal_other'
}
